// No 'credit' mode (ops simulation T08): a sale on account is the
// "Account Credit" payment method on the normal payment screen.
export type BillMode = 'sale' | 'return' | 'quotation';

/** A mode stored before T08 (or unknown) falls back to Sale. */
export function toBillMode(value: unknown): BillMode {
  return value === 'return' || value === 'quotation' ? value : 'sale';
}

export type BillModeCtaVariant = 'primary' | 'danger';

/**
 * Per-mode metadata consumed by both the mode pills and the bottom CTA so the
 * two can never drift apart. `ctaLabel` is a function so callers can decide
 * whether to interpolate the total or not — modes where no money moves
 * (quotation, credit) deliberately omit it.
 */
/**
 * Permission keys honoured by the mode pills. Map to the booleans in the
 * profile's `permissions` block. Callers pass a `permissions` object into
 * the selector; modes requesting a key not present in the bag are hidden.
 */
export type BillModePermission = 'can_issue_credit_note';

export interface BillModeMeta {
  /** Stable id; matches the BillMode union. */
  id: BillMode;
  /** Pill text. */
  label: string;
  /** Tailwind active-state classes for the pill. */
  activeClasses: string;
  /** Faint banner tint shown above the cart for non-sale modes. */
  bannerTint: string;
  /** One-line helper text shown in the banner. Null for `sale` (no banner). */
  bannerHelper: string | null;
  /** Build the bottom CTA label. `formattedTotal` is e.g. "₹533.00". */
  ctaLabel: (formattedTotal: string) => string;
  /** Whether the CTA renders in danger styling. */
  ctaVariant: BillModeCtaVariant;
  /** True if this mode actually transfers money at checkout — used to
   *  decide whether to show round-off, payment-method chips, etc. */
  movesMoneyOnCheckout: boolean;
  /** Optional permission key the user must hold for this mode to appear. */
  requires?: BillModePermission;
}

export const BILL_MODE_META: Record<BillMode, BillModeMeta> = {
  sale: {
    id: 'sale',
    label: 'Sale',
    activeClasses: 'bg-brand-500 text-white',
    bannerTint: '',
    bannerHelper: null,
    ctaLabel: (total) => `Pay ${total}`,
    ctaVariant: 'primary',
    movesMoneyOnCheckout: true,
  },
  quotation: {
    id: 'quotation',
    label: 'Quote',
    activeClasses: 'bg-info-500 text-white',
    bannerTint: 'bg-info-50 text-info-700 dark:bg-info-900/30 dark:text-info-200',
    bannerHelper: 'Quote mode — no payment is captured, stock is not deducted',
    ctaLabel: () => 'Save Quote',
    ctaVariant: 'primary',
    movesMoneyOnCheckout: false,
  },
  return: {
    id: 'return',
    label: 'Return',
    activeClasses: 'bg-danger-500 text-white',
    bannerTint: 'bg-danger-50 text-danger-700 dark:bg-danger-900/30 dark:text-danger-200',
    bannerHelper: 'Return mode — refund or credit is issued against the original bill',
    ctaLabel: (total) => `Refund ${total}`,
    ctaVariant: 'danger',
    movesMoneyOnCheckout: true,
    // Tenant-configurable. Hidden when the cashier's role is below the
    // tenant's return floor (see ReturnPolicyService server-side).
    requires: 'can_issue_credit_note',
  },
};

/** Ordered list for tab rendering — keeps Sale first as the safe default. */
export const BILL_MODE_ORDER: ReadonlyArray<BillMode> = ['sale', 'quotation', 'return'];

export interface BillModeSelectorProps {
  value: BillMode;
  onChange: (mode: BillMode) => void;
  disabled?: boolean;
  /**
   * Map of permission booleans, sourced from the user profile. Modes
   * declaring a `requires` key are filtered out when the value is falsy.
   * Pass `undefined` (or omit) to render every mode — backwards-compatible
   * with callers that haven't wired permissions yet.
   */
  permissions?: Partial<Record<BillModePermission, boolean>>;
  /**
   * Modes outside the business's plan. They stay visible with a lock mark
   * (lock, never hide); a click still calls `onChange`, so the caller decides
   * what to show instead of switching.
   */
  lockedModes?: ReadonlyArray<BillMode>;
  className?: string;
}
